import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Section from 'components/common/Section';
import SectionHeader from './SectionHeader';
import { useTranslation } from 'react-i18next';
import { fetchPublicPlans } from 'helpers/plans';

const CURRENCY_MAP = {
  AR: { key: 'price_ars', symbol: '$', suffix: 'ARS' },
  BR: { key: 'price_brl', symbol: 'R$', suffix: 'BRL' },
  US: { key: 'price_usd', symbol: '$', suffix: 'USD' },
  CL: { key: 'price_clp', symbol: '$', suffix: 'CLP' },
  CN: { key: 'price_cny', symbol: '¥', suffix: 'CNY' },
};

const USD_CURRENCY = { key: 'price_usd', symbol: '$', suffix: 'USD' };
const DEFAULT_COUNTRY_DISPLAY = { show_usd: true, show_local: false };

const resolveCountryFromLocale = value => {
  const lang = String(value || '').toLowerCase();
  if (!lang) return '';
  if (lang.includes('es-cl') || lang.endsWith('-cl')) return 'CL';
  if (lang.includes('es-ar') || lang.endsWith('-ar')) return 'AR';
  if (lang.includes('pt-br') || lang.endsWith('-br')) return 'BR';
  if (lang.includes('en-us') || lang.endsWith('-us')) return 'US';
  if (lang.startsWith('zh') || lang.includes('-cn') || lang.endsWith('-cn')) return 'CN';
  if (lang.startsWith('es')) return 'AR';
  if (lang.startsWith('pt')) return 'BR';
  return '';
};

const resolveCountryFromTimeZone = value => {
  const zone = String(value || '').toLowerCase();
  if (!zone) return '';
  if (zone.includes('argentina') || zone.includes('buenos_aires')) return 'AR';
  if (zone.includes('sao_paulo') || zone.includes('brazil')) return 'BR';
  if (zone.includes('santiago') || zone.includes('chile')) return 'CL';
  if (zone.includes('shanghai') || zone.includes('beijing') || zone.includes('hong_kong') || zone.includes('china')) return 'CN';
  if (zone.includes('new_york') || zone.includes('chicago') || zone.includes('denver') || zone.includes('los_angeles') || zone.includes('us/')) return 'US';
  return '';
};

const LANG_MAP = { en: 'en', es: 'es', pt: 'br', 'pt-BR': 'br', zh: 'zh' };

const FEATURE_CHECKS = [
  { key: 'max_questions_per_month', labelKey: 'PRICING_FEAT_QUESTIONS', format: v => v === 0 ? '∞' : v },
  { key: 'max_ml_accounts', labelKey: 'PRICING_FEAT_ML_ACCOUNTS', format: v => v },
  { key: 'max_users', labelKey: 'PRICING_FEAT_USERS', format: v => v },
  { key: 'max_agents', labelKey: 'PRICING_FEAT_AGENTS', format: v => v },
  { key: 'max_agent_docs_per_product', labelKey: 'PRICING_FEAT_AGENT_DOCS', format: v => v },
  { key: 'data_retention_days', labelKey: 'PRICING_FEAT_RETENTION', format: v => v === 0 ? '∞' : `${v}d` },
];

const BOOLEAN_FEATURES = [
  { key: 'has_analytics', labelKey: 'PRICING_FEAT_ANALYTICS' },
  { key: 'has_stock_module', labelKey: 'PRICING_FEAT_STOCK' },
  { key: 'has_claims_module', labelKey: 'PRICING_FEAT_CLAIMS' },
  { key: 'has_calendar_module', labelKey: 'PRICING_FEAT_CALENDAR' },
  { key: 'has_whatsapp', labelKey: 'PRICING_FEAT_WHATSAPP' },
  { key: 'has_agent_customization', labelKey: 'PRICING_FEAT_CUSTOMIZATION' },
  { key: 'has_priority_support', labelKey: 'PRICING_FEAT_PRIORITY' },
];

const LandingPricing = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [country, setCountry] = useState('US');

  useEffect(() => {
    fetchPublicPlans()
      .then(data => setPlans(data.plans || []))
      .catch(() => {});
  }, []);

  // Detect country from browser/i18n locale with sensible regional fallback.
  useEffect(() => {
    const navigatorCandidates = [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
    ].filter(Boolean);
    const i18nCandidates = [
      i18n.language,
      ...(Array.isArray(i18n.languages) ? i18n.languages : []),
    ].filter(Boolean);

    const candidates = [...navigatorCandidates, ...i18nCandidates];
    const detectedFromLocale = candidates.map(resolveCountryFromLocale).find(Boolean);
    const detectedFromTimeZone = resolveCountryFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const detected = detectedFromLocale || detectedFromTimeZone || 'US';
    if (detected) {
      setCountry(detected);
    }
  }, [i18n.language, i18n.languages]);

  // Resolve localized text helper
  const langCode = LANG_MAP[i18n.language] || LANG_MAP[i18n.language?.split('-')[0]] || 'en';
  const localizedName = (plan) =>
    plan[`name_${langCode}`] || plan.name_en || plan.name;
  const localizedDesc = (plan) =>
    plan[`description_${langCode}`] || plan.description_en || plan.description;

  const localCurrency = CURRENCY_MAP[country] || CURRENCY_MAP.US;

  // Check if any plan has yearly enabled
  const hasYearly = plans.some(p => p.enable_yearly);

  /**
   * Calculate the per-month price for yearly billing.
   * If yearly_price_usd_override > 0, use that directly as the per-month price.
   * Otherwise, apply yearly_discount_pct to the monthly price.
   */
  const getYearlyMonthlyUsd = (plan) => {
    const override = parseFloat(plan.yearly_price_usd_override || 0);
    if (override > 0) return override;
    const monthly = parseFloat(plan.price_usd || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  const getYearlyMonthlyLocal = (plan, currencyKey) => {
    // For local currencies we apply the same discount percentage
    const monthly = parseFloat(plan[currencyKey] || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  if (plans.length === 0) return null;

  return (
    <Section bg="light" className="text-center py-6" id="pricing">
      <SectionHeader
        title={t('LANDING.PRICING_TITLE')}
        subtitle={t('LANDING.PRICING_SUBTITLE')}
      />

      {/* Billing toggle */}
      {hasYearly && (
        <div className="d-flex justify-content-center mt-4 mb-2">
          <ToggleButtonGroup
            type="radio"
            name="billing"
            value={billing}
            onChange={v => setBilling(v)}
          >
            <ToggleButton
              id="billing-monthly"
              variant={billing === 'monthly' ? 'primary' : 'outline-primary'}
              value="monthly"
              className="px-4"
            >
              {t('LANDING.PRICING_MONTHLY')}
            </ToggleButton>
            <ToggleButton
              id="billing-yearly"
              variant={billing === 'yearly' ? 'primary' : 'outline-primary'}
              value="yearly"
              className="px-4"
            >
              {t('LANDING.PRICING_YEARLY')}
              <Badge bg="success" className="ms-2">{t('LANDING.PRICING_SAVE')}</Badge>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      <div className="d-flex justify-content-center mb-3">
        <div style={{ minWidth: 220, textAlign: 'left' }}>
          <Form.Label className="mb-1 small text-muted">País / moneda</Form.Label>
          <Form.Select size="sm" value={country} onChange={event => setCountry(event.target.value)}>
            {Object.keys(CURRENCY_MAP).map(code => (
              <option key={code} value={code}>
                {code} - {CURRENCY_MAP[code].suffix}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <Row className="g-4 mt-3 justify-content-center">
        {plans.map((plan, index) => {
          const pricingDisplay = plan.pricing_display || {};
          const countryDisplay = pricingDisplay[country] || DEFAULT_COUNTRY_DISPLAY;
          const showUsdConfigured = countryDisplay.show_usd !== false;
          const showLocalConfigured = countryDisplay.show_local === true && country !== 'US';

          const isYearly = billing === 'yearly' && plan.enable_yearly;

          const usdMonthly = parseFloat(plan.price_usd || 0);
          const usdPrice = isYearly ? getYearlyMonthlyUsd(plan) : usdMonthly;
          const localPrice = isYearly
            ? getYearlyMonthlyLocal(plan, localCurrency.key)
            : parseFloat(plan[localCurrency.key] || 0);
          const hasUsdPrice = usdPrice > 0;
          const hasLocalPrice = localPrice > 0;

          const showUsdPrimary = (showUsdConfigured && hasUsdPrice)
            || ((!showUsdConfigured || !showUsdConfigured && !showLocalConfigured) && !hasLocalPrice && hasUsdPrice)
            || (!showLocalConfigured && hasUsdPrice);
          const showLocalPrimary = !showUsdPrimary && showLocalConfigured && hasLocalPrice;
          const showSecondaryLocal = showUsdPrimary && showLocalConfigured && hasLocalPrice;

          const isFeatured = index === 1 || plan.slug === 'professional';
          const isEnterprise = plan.slug === 'enterprise';

          return (
            <Col key={plan.slug} lg={3} md={6} sm={12}>
              <div
                className={`card h-100 shadow-sm border-0 ${isFeatured ? 'border-primary border-2 shadow' : ''}`}
                style={{
                  borderRadius: '1rem',
                  ...(isFeatured ? { border: '2px solid var(--falcon-primary)', transform: 'scale(1.03)' } : {}),
                }}
              >
                {isFeatured && (
                  <div
                    className="bg-primary text-white text-center py-1 fw-bold"
                    style={{ borderRadius: '1rem 1rem 0 0', fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {t('LANDING.PRICING_POPULAR')}
                  </div>
                )}
                <div className="card-body d-flex flex-column p-4">
                  <h4 className="fw-bold mb-1">{localizedName(plan)}</h4>
                  <p className="text-muted small mb-3">{localizedDesc(plan)}</p>

                  {/* Price — always per month */}
                  <div className="mb-4">
                    {isEnterprise ? (
                      <h2 className="fw-bold mb-0">{t('LANDING.PRICING_CUSTOM')}</h2>
                    ) : (
                      <>
                        {showUsdPrimary && (
                          <h2 className="fw-bold mb-0">
                            <span className="fs-2 align-top">{USD_CURRENCY.symbol}</span>
                            {usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small className="text-muted fs--1 fw-normal">
                              {' '}{USD_CURRENCY.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </h2>
                        )}
                        {showLocalPrimary && (
                          <h2 className="fw-bold mb-0">
                            <span className="fs-2 align-top">{localCurrency.symbol}</span>
                            {localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small className="text-muted fs--1 fw-normal">
                              {' '}{localCurrency.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </h2>
                        )}
                        {showSecondaryLocal && (
                          <p className="text-muted small mb-0">
                            {`≈ ${localCurrency.symbol}`}
                            {localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small>
                              {' '}{localCurrency.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </p>
                        )}
                        {!showUsdPrimary && !showLocalPrimary && (
                          <p className="text-muted small mb-0">{t('LANDING.PRICING_CUSTOM')}</p>
                        )}
                        {/* "Billed annually" note when yearly is selected */}
                        {isYearly && (
                          <p className="text-muted small mb-0 mt-1">
                            {t('LANDING.PRICING_BILLED_ANNUALLY')}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <Button
                    as={Link}
                    to={isEnterprise ? '/waitlist' : '/login'}
                    variant={isFeatured ? 'primary' : 'outline-primary'}
                    className="w-100 rounded-pill mb-4 fw-semi-bold"
                  >
                    {isEnterprise ? t('LANDING.PRICING_CONTACT') : t('LANDING.PRICING_GET_STARTED')}
                  </Button>

                  {/* Features */}
                  <ul className="list-unstyled text-start flex-grow-1">
                    {FEATURE_CHECKS.map(feat => (
                      <li key={feat.key} className="py-1 d-flex align-items-start">
                        <FontAwesomeIcon icon="check" className="text-success me-2 mt-1 flex-shrink-0" />
                        <span>
                          <strong>{feat.format(plan[feat.key])}</strong> {t(`LANDING.${feat.labelKey}`)}
                        </span>
                      </li>
                    ))}
                    {BOOLEAN_FEATURES.map(feat => (
                      <li key={feat.key} className="py-1 d-flex align-items-start">
                        {plan[feat.key] ? (
                          <FontAwesomeIcon icon="check" className="text-success me-2 mt-1 flex-shrink-0" />
                        ) : (
                          <FontAwesomeIcon icon="times" className="text-300 me-2 mt-1 flex-shrink-0" />
                        )}
                        <span className={plan[feat.key] ? '' : 'text-400'}>
                          {t(`LANDING.${feat.labelKey}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Section>
  );
};

export default LandingPricing;
